/**
 * Fatture › Chi ci deve soldi (26/10/2026, os-planning/ridisegno/CAPITOLATO.md).
 *
 * La domanda: chi ci deve soldi e da quanto?
 * La forma: un cliente per riga (logo o iniziali), quanto deve e da quanto; se ha più rate, le rate
 * sotto di lui. I gesti stanno sulla riga: segna incassata, sollecita, e nel ⋯ il resto.
 *
 * Prima (FattureDaIncassare.tsx, che resta nel codice ma non si mostra più): una frase, una fascia
 * di tre numeri, le linguette, una barra con ricerca, ordina, filtra e "Altri elenchi", e una tabella
 * a gruppi con le caselle per togliere più fatture insieme. Tolti dalla vista (si rimettono da lì):
 * la fascia, la ricerca, ordina e filtra, la selezione multipla. "Togli dall'elenco" resta nel ⋯.
 *
 * Il bonifico che torna (admin.autochiusura.ts) resta sulla riga: "In banca c'è il bonifico,
 * basta confermarlo", con il motivo scritto quando non torna del tutto.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../../components/ui/Icon';
import { BrandLogo } from '../../../components/ui/BrandLogo';
import { ActionsMenu, type ActionItem } from '../../../components/ui/ActionsMenu';
import { leggibile } from '../../../components/mobile/MobileKit';
import { fmtEur, fmtDate } from '../../../lib/utils';
import { adminHref } from '../../../lib/adminRoutes';
import { isoDay } from '../adminApi';
import { useAdminActions } from '../useAdminActions';
import { SettlePopover } from '../SettlePopover';
import { TrashPopover } from '../TrashPopover';
import { SollecitaPopover } from './SollecitaPopover';
import {
  durata, etichettaRata, giorniDaOggi, giornoInParole, ilGiorno, alGiorno, dalGiorno, delGiorno, quando, rataInParole,
  type DaIncassare, type RataDaIncassare,
} from './fattureApi';

const NOTA_CONFERMA = 'Confermata da Fatture: in banca c\'è il bonifico corrispondente.';

/** Il bonifico che non torna del tutto, con il motivo scritto dall'API. */
interface DubbioRata { txId: string; settledOn: string | null; amount: number; counterparty: string | null; label: string; testo: string; scegli: boolean }
const dubbioDi = (r: RataDaIncassare): DubbioRata | null => (r as RataDaIncassare & { dubbio?: DubbioRata | null }).dubbio ?? null;

export interface ClienteCheDeve {
  id: string;
  nome: string;
  rate: RataDaIncassare[];
  totale: number;
  /** Giorni di ritardo della rata più vecchia (0 se nessuna è in ritardo). */
  ritardo: number;
}

/** Le rate messe insieme per cliente: prima chi è in ritardo da più tempo, poi chi scade prima. */
export function raggruppaPerCliente(items: RataDaIncassare[]): ClienteCheDeve[] {
  const m = new Map<string, ClienteCheDeve>();
  for (const r of items) {
    const c = m.get(r.clientId) ?? { id: r.clientId, nome: r.clientName, rate: [], totale: 0, ritardo: 0 };
    c.rate.push(r);
    c.totale += r.amount;
    m.set(r.clientId, c);
  }
  const prima = (c: ClienteCheDeve) => c.rate[0]?.dueDate ?? '9999-12-31';
  for (const c of m.values()) {
    c.rate.sort((a, b) => (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'));
    const g = giorniDaOggi(c.rate[0]?.dueDate);
    c.ritardo = g != null && g < 0 ? -g : 0;
  }
  return [...m.values()].sort((a, b) => b.ritardo - a.ritardo || prima(a).localeCompare(prima(b)));
}

export function ChiCiDeve({ data, canWrite, canReadDocs }: { data: DaIncassare; canWrite: boolean; canReadDocs: boolean }) {
  const clienti = useMemo(() => raggruppaPerCliente(data.items), [data]);

  if (!clienti.length) {
    return (
      <p className="ft-tuttook"><Icon name="check" size={14} /> Nessun cliente ci deve soldi: tutte le fatture emesse risultano incassate.</p>
    );
  }

  return (
    <div className="ft-clienti">
      {clienti.map((c) => (
        <div key={c.id} className="ft-cliente">
          {c.rate.length > 1 && (
            <div className="ft-riga ft-riga--testa">
              <BrandLogo name={c.nome} size={36} />
              <span className="ft-chi">
                <span className="t">{leggibile(c.nome)}</span>
                <span className="s">{c.rate.length} rate da incassare</span>
              </span>
              <span />
              <span className="ft-cifra">€ {fmtEur(c.totale, true)}</span>
              <span />
            </div>
          )}
          {c.rate.map((r) => (
            <RigaRata key={r.id} r={r} conCliente={c.rate.length === 1} canWrite={canWrite} canReadDocs={canReadDocs} />
          ))}
        </div>
      ))}
    </div>
  );
}

function RigaRata({ r, conCliente, canWrite, canReadDocs }: { r: RataDaIncassare; conCliente: boolean; canWrite: boolean; canReadDocs: boolean }) {
  const navigate = useNavigate();
  const { markPaid, confermaProposta, ignoreDocs } = useAdminActions();
  const [aperto, setAperto] = useState<null | 'incassa' | 'sollecita' | 'togli'>(null);
  const g = giorniDaOggi(r.dueDate);
  const tardi = g != null && g < 0;
  const rata = rataInParole(r.seq, r.ofTotal);
  const label = etichettaRata(r);
  const b = r.bonifico;
  const dubbio = dubbioDi(r);
  const fattura = <>Fattura {r.invoiceNumber ?? 'senza numero'}{rata ? `, ${rata}` : ''}</>;

  const menu: ActionItem[] = [
    { key: 'apri', label: 'Apri la fattura', icon: 'file', onClick: () => navigate(`/invoices/${r.invoiceId}`), hidden: !canReadDocs },
    { key: 'sollecita', label: 'Prepara il testo del sollecito', icon: 'mail', onClick: () => setAperto('sollecita'), hidden: tardi },
    { key: 'togli', label: "Togli la fattura dall'elenco", icon: 'trash', danger: true, onClick: () => setAperto('togli'), hidden: !canWrite },
  ];

  return (
    <div className={`ft-riga${conCliente ? '' : ' is-rata'}`}>
      {conCliente ? <BrandLogo name={r.clientName} size={36} /> : <span aria-hidden />}
      <span className="ft-chi">
        {conCliente && <span className="t">{leggibile(r.clientName)}</span>}
        <span className={conCliente ? 's' : 't'}>
          {canReadDocs
            ? <a className="ft-link-fattura" onClick={() => navigate(`/invoices/${r.invoiceId}`)}>{fattura}</a>
            : fattura}
        </span>
      </span>
      <span className={`ft-quando${tardi ? ' is-alert' : ''}`} title={r.dueDate ? `Da pagare entro il ${fmtDate(r.dueDate)}` : undefined}>
        <span className="t">{tardi ? `in ritardo da ${durata(g!)}` : r.dueDate ? `scade ${quando(r.dueDate)}` : 'senza scadenza'}</span>
        {r.dueDate && <span className="s">{tardi ? `scaduta ${ilGiorno(r.dueDate)}` : `${ilGiorno(r.dueDate)}`}</span>}
      </span>
      <span className="ft-cifra">€ {fmtEur(r.amount, true)}</span>
      <span className="ft-azioni">
        {canWrite && (
          <button className="btn btn-sm" disabled={markPaid.isPending} title="Registra l'incasso di questa rata: scegli la data, il modo e il bonifico"
            onClick={() => setAperto(aperto === 'incassa' ? null : 'incassa')}>
            <Icon name="check" size={10} /> Segna incassata
          </button>
        )}
        {tardi && (
          <button className="btn btn-sm btn-ghost" title="Prepara il testo del sollecito da copiare: da qui non parte nessuna mail"
            onClick={() => setAperto(aperto === 'sollecita' ? null : 'sollecita')}>
            <Icon name="mail" size={10} /> Sollecita
          </button>
        )}
        <ActionsMenu items={menu} label={`Altre azioni su ${label}`} />
        {aperto === 'incassa' && (
          <SettlePopover
            mode="incassa"
            amount={r.amount}
            subtitle={rata ? `${rata} della fattura ${r.invoiceNumber ?? ''}` : `fattura ${r.invoiceNumber ?? ''}`}
            target={{ kind: 'INVOICE_PAYMENT', id: r.id }}
            withNote
            pending={markPaid.isPending}
            onClose={() => setAperto(null)}
            onConfirm={(v) => { setAperto(null); markPaid.mutate({ paymentId: r.id, amount: r.amount, label, ...v }); }}
          />
        )}
        {aperto === 'sollecita' && <SollecitaPopover rata={r} onClose={() => setAperto(null)} />}
        {aperto === 'togli' && (
          <TrashPopover
            title={r.invoiceNumber ?? 's/n'}
            pending={ignoreDocs.isPending}
            onClose={() => setAperto(null)}
            onConfirm={(v) => { setAperto(null); ignoreDocs.mutate({ kind: 'invoice', ids: [r.invoiceId], label, ...v }); }}
          />
        )}
      </span>

      {/* In banca c'è già il bonifico: basta confermarlo (o il motivo per cui non torna del tutto). */}
      {(b || dubbio) && (
        <div className={`ft-banca${b ? '' : ' is-dubbio'}`}>
          <Icon name={b ? 'banknote' : 'alert'} size={13} />
          <span className="ft-banca-tx">
            {b
              ? <>In banca c'è il bonifico di {b.counterparty ?? 'questo cliente'} {delGiorno(b.settledOn)}, € {fmtEur(b.amount, true)}: torna con la rata.</>
              : dubbio!.testo}
          </span>
          {canWrite && (b || (dubbio && !dubbio.scegli)) && (
            <button className="btn btn-sm" disabled={confermaProposta.isPending}
              onClick={() => {
                const tx = b ?? dubbio!;
                confermaProposta.mutate({ lato: 'INCASSO', rataId: r.id, txId: tx.txId, paidDate: tx.settledOn ?? isoDay(), label, importo: r.amount, nota: NOTA_CONFERMA });
              }}>
              <Icon name="check" size={10} /> Conferma l'incasso
            </button>
          )}
          {canWrite && !b && dubbio?.scegli && (
            <button className="btn btn-sm" title="Apri i movimenti del conto di questo cliente e collega quello giusto"
              onClick={() => navigate(adminHref('banca', { vista: 'movimenti', q: dubbio.counterparty ?? r.clientName, side: 'CREDIT', unmatched: '1' }))}>
              <Icon name="link" size={10} /> Scegli il bonifico
            </button>
          )}
        </div>
      )}
    </div>
  );
}
